"use client";

import { Home, Building2, Building, Quote } from "lucide-react";
import { useI18n } from "@/lib/i18n/I18nProvider";

const INTEGRATIONS_RU = [
  "1С:Отель",
  "1С:Бухгалтерия",
  "iiko",
  "TravelLine",
  "R-Keeper 7",
  "Logus HMS",
];

const INTEGRATIONS_EN = [
  "Opera PMS",
  "Fidelio",
  "Mews",
  "Logus HMS",
  "1C",
  "iiko",
];

export function TestimonialsSection() {
  const { t, locale } = useI18n();
  const INTEGRATIONS = locale === "en" ? INTEGRATIONS_EN : INTEGRATIONS_RU;

  const TESTIMONIALS = [
    {
      quote: t("landing.testimonials.t1Quote"),
      role: t("landing.testimonials.t1Role"),
      property: t("landing.testimonials.t1Property"),
    },
    {
      quote: t("landing.testimonials.t2Quote"),
      role: t("landing.testimonials.t2Role"),
      property: t("landing.testimonials.t2Property"),
    },
    {
      quote: t("landing.testimonials.t3Quote"),
      role: t("landing.testimonials.t3Role"),
      property: t("landing.testimonials.t3Property"),
    },
  ];

  const USE_CASES = [
    {
      icon: Home,
      title: t("landing.socialProof.single"),
      description: t("landing.socialProof.singlePunch"),
      details: t("landing.socialProof.singleDesc"),
    },
    {
      icon: Building2,
      title: t("landing.socialProof.chain"),
      description: t("landing.socialProof.chainPunch"),
      details: t("landing.socialProof.chainDesc"),
    },
    {
      icon: Building,
      title: t("landing.socialProof.group"),
      description: t("landing.socialProof.groupPunch"),
      details: t("landing.socialProof.groupDesc"),
    },
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-5xl mx-auto">
        {/* Testimonials header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-keiro-obsidian mb-4">
            {t("landing.testimonials.sectionTitle")}
          </h2>
          <p className="text-lg text-keiro-gray-600">
            {t("landing.testimonials.sectionSubtitle")}
          </p>
        </div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {TESTIMONIALS.map((item, index) => (
            <div
              key={index}
              className="bg-slate-50 rounded-xl p-6 border border-slate-200 flex flex-col"
            >
              <Quote className="w-6 h-6 text-keiro-green mb-4 flex-shrink-0" />
              <p className="text-slate-700 leading-relaxed text-sm flex-1">
                {item.quote}
              </p>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-semibold text-keiro-obsidian">
                  {item.role}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{item.property}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Use cases header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-keiro-obsidian mb-4">
            {t("landing.socialProof.title")}
          </h2>
          <p className="text-lg text-keiro-gray-600">
            {t("landing.socialProof.subtitle")}
          </p>
        </div>

        {/* Use case cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {USE_CASES.map((useCase, index) => {
            const Icon = useCase.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow"
              >
                <div className="mb-4">
                  <Icon className="w-8 h-8 text-keiro-green" />
                </div>
                <h3 className="text-lg font-semibold text-keiro-obsidian mb-2">
                  {useCase.title}
                </h3>
                <p className="text-sm font-medium text-keiro-green mb-4">
                  {useCase.description}
                </p>
                <p className="text-sm text-keiro-gray-600 leading-relaxed">
                  {useCase.details}
                </p>
              </div>
            );
          })}
        </div>

        {/* Integrations footer */}
        <div className="border-t border-slate-200 pt-12">
          <p className="text-center text-sm text-keiro-gray-600 mb-6">
            <span className="font-semibold text-keiro-obsidian">
              {t("landing.socialProof.integratesWith")}
            </span>
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {INTEGRATIONS.map((integration, index) => (
              <div
                key={index}
                className="px-4 py-2 bg-keiro-gray-100 rounded-lg text-sm text-keiro-obsidian font-medium"
              >
                {integration}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
